import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  DotNestedKeys,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CostType, CostTypeVersion, UpsertCostType } from "./types";

export const useCostTypesApi = () => {
  const api = useApi();

  const getCostType = useCallback(
    (costTypeId: string, signal: AbortSignal) => {
      const url = `/admin/cost_types/${costTypeId}`;
      return useSWRMutation<CostType | null, CustomError, string>(url, (url) => api.get<CostType>({ url, signal }));
    },
    [api],
  );

  const useAdminCostTypes = useCallback(
    <K extends DotNestedKeys<CostType>>(
      query: PaginationQueryWithFields<NestedPick<CostType, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<NestedPick<CostType, K>>("/admin/cost_types", query);
      return useSWR<Paginated<NestedPick<CostType, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CostType, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCostType = useCallback(
    async (body: UpsertCostType): Promise<{ id: string }> => {
      const url = `/admin/cost_types`;
      const id = await api.post<UpsertCostType, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminCostType = useCallback(
    async (id: string, body: UpsertCostType) => {
      const url = `/admin/cost_types/${id}`;
      await api.put<UpsertCostType>(url, body);
      api.mutateCache([`/admin/costs`, `/admin/cost_types`, `/admin/cost_types/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminCostType = useCallback(
    async (costTypeId: string): Promise<void> => {
      const url = `/admin/cost_types/${costTypeId}`;
      await api.del(url);
      api.mutateCache([`/admin/cost_types`]);
    },
    [api],
  );

  const getAdminCostTypeHistory = useCallback(
    (query: PaginationQuery, costTypeID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/cost_types/${costTypeID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCostTypeVersion = useCallback(
    (costTypeId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/cost_types/${costTypeId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CostTypeVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CostTypeVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/cost_types/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminCostType,
    updateAdminCostType,
    insertAdminCostType,
    getCostType,
    useAdminCostTypes,
    getAdminCostTypeHistory,
    getAdminCostTypeVersion,
    getFieldFilters,
  };
};
